<template>
  <div></div>
</template>

<script setup lang="ts">
import { createVNode, getCurrentInstance, onMounted, render } from "vue";

import type { Newsletter } from "@/types/newsletter";

import type { SubscriberFacingAuthentication } from "../SubscribeForm/lib";
import SurveyWidget from "./SurveyWidget.vue";

const props = defineProps<{
  newsletter: Newsletter;
  subscriberFacingAuthentication: SubscriberFacingAuthentication;
  emailId: string | null;
}>();

const extractEmailIdFromUrl = (url: string): string | null => {
  // Parse URLs like: /username/subscribers/{sub_id}/emails/{email_id}/surveys/{survey_id}/...
  const match = url.match(/\/emails\/([^/]+)\/surveys\//);
  return match ? match[1] : null;
};

const extractSurveyData = (
  element: Element
): {
  surveyId: string;
  emailId: string;
  question: string;
  answers: string[];
} | null => {
  const surveyId = element.getAttribute("data-survey-id");
  if (!surveyId) return null;

  // Extract question text
  const questionElement = element.querySelector(".survey__question-text");
  const question = questionElement?.textContent?.trim() || "";

  // Extract answers from links
  const answerLinks = element.querySelectorAll(".survey__answer-item a");
  const answers: string[] = [];
  let emailId: string | null = null;

  answerLinks.forEach((link) => {
    const text = link.textContent?.trim();
    if (text) {
      answers.push(text);
    }

    // Extract email_id from the first link
    if (!emailId) {
      const href = link.getAttribute("href");
      if (href) {
        emailId = extractEmailIdFromUrl(href);
      }
    }
  });

  // Fall back to prop emailId if we couldn't extract from links
  if (!emailId) {
    emailId = props.emailId;
  }

  if (!emailId || answers.length === 0) {
    return null;
  }

  return { surveyId, emailId, question, answers };
};

onMounted(() => {
  // Find all survey elements in the document
  const surveyElements = document.querySelectorAll("[data-survey-id]");

  // Get the subscriber for this newsletter
  const subscriber = props.subscriberFacingAuthentication.subscribers?.find(
    (sub) => sub.newsletter_id === props.newsletter.id
  );

  surveyElements.forEach((surveyElement) => {
    const surveyData = extractSurveyData(surveyElement);
    if (!surveyData) return;

    // Create a container for the Vue component
    const container = document.createElement("div");
    container.className = "survey-widget-container";

    // Replace the original survey element with the container
    surveyElement.replaceWith(container);

    // Mount the Vue component
    const vnode = createVNode(SurveyWidget, {
      surveyId: surveyData.surveyId,
      emailId: surveyData.emailId,
      question: surveyData.question,
      answers: surveyData.answers,
      subscriber: subscriber,
      newsletter: props.newsletter,
      subscriberFacingAuthentication: props.subscriberFacingAuthentication,
    });
    vnode.appContext = getCurrentInstance()?.appContext || null;
    render(vnode, container);
  });
});
</script>
